import React, { useCallback, useEffect, useState } from "react";
import { HandLeftFilled } from "@fluentui/react-icons";
import { Badge, Button, makeStyles, mergeClasses, Tooltip } from "@fluentui/react-components";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

const useClasses = makeStyles({
  buttonPressed: {
    backgroundColor: "var(--primaryColorBackground)"
  },
  notificationBubble: {
    position: "absolute",
    top: "-4px",
    right: 0
  },
  buttonWrapper: {
    position: "relative",
    display: "inline-block"
  }
});

export function ToolbarRaiseHandButton({ styles, onToggleHandRaised, active, raisedHands }) {
  const intl = useIntl();
  const classes = useClasses();

  const [raisedHandsCount, setRaisedHandsCount] = useState(0);

  useEffect(() => {
    setRaisedHandsCount(raisedHands.length);
  }, [raisedHands]);

  const [raisedHandsText, setRaisedHandsText] = useState("");

  useEffect(() => {
    const raisedHandsNames = raisedHands.map(hand => hand.displayName);
    let message;

    if (raisedHandsNames.length === 0) {
      message = intl.formatMessage({
        id: "reaction-popover.no-hand-raised",
        defaultMessage: "No hands are currently raised"
      });
    } else if (raisedHandsNames.length === 1) {
      message = intl.formatMessage(
        { id: "reaction-popover.hand-raised-warning-singular", defaultMessage: "{raisedHands} has raised their hand" },
        { raisedHands: raisedHandsNames[0] }
      );
    } else if (raisedHandsNames.length <= 3) {
      message = intl.formatMessage(
        { id: "reaction-popover.hand-raised-warning-plural", defaultMessage: "{raisedHands} have raised their hands" },
        { raisedHands: raisedHandsNames.join(", ") }
      );
    } else {
      const topThree = raisedHandsNames.slice(0, 3).join(", ");
      message = intl.formatMessage(
        {
          id: "reaction-popover.hand-raised-warning-and-others",
          defaultMessage: "{raisedHands} and others have raised their hands"
        },
        { raisedHands: topThree }
      );
    }

    setRaisedHandsText(message);
  }, [raisedHands, intl]);

  return (
    <Tooltip content={raisedHandsText}>
      <div className={classes.buttonWrapper}>
        {raisedHandsCount > 0 && (
          <Badge size="small" color="brand" shape="circular" appearance="filled" className={classes.notificationBubble}>
            {raisedHandsCount > 99 ? "+99" : raisedHandsCount}
          </Badge>
        )}
        <Button
          appearance="subtle"
          icon={<HandLeftFilled width={24} height={24} />}
          onClick={onToggleHandRaised}
          title={
            active
              ? intl.formatMessage({
                  id: "reaction-popover.hand-raised-warning",
                  defaultMessage: "Your hand is raised"
                })
              : intl.formatMessage({ id: "toolbar.raise-hand", defaultMessage: "Raise hand" })
          }
          className={active ? mergeClasses(styles.button, classes.buttonPressed) : styles.button}
        />
      </div>
    </Tooltip>
  );
}

ToolbarRaiseHandButton.propTypes = {
  styles: PropTypes.object,
  onToggleHandRaised: PropTypes.func,
  active: PropTypes.bool,
  raisedHands: PropTypes.array
};
